import { useState, useEffect, useRef, useCallback } from 'react';
import { apiClientV2 } from '../services/apiClientV2';

const useAutocomplete = (type, options = {}) => {
  const {
    debounceMs = 300,
    minLength = 2,
    pageSize = 10,
    onSelect = null,
    disabled = false
  } = options;

  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  
  const debounceRef = useRef(null);
  const requestRef = useRef(null);

  const search = useCallback(async (searchQuery, page = 1, resetSuggestions = true) => {
    if (!searchQuery || searchQuery.length < minLength || disabled) {
      setSuggestions([]);
      setTotalCount(0);
      setHasMore(false);
      return;
    }

    if (requestRef.current) {
      requestRef.current.cancel?.();
    }

    setLoading(true);

    try {
      let response;
      const params = {
        limit: pageSize,
        page: page
      };

      // Most-connected first, so "Asurion" (hundreds of people) leads "ASURION LIMITED" (one)
      params.sort = 'contact_count';
      params.order = 'desc';

      if (type === 'company') {
        params.filter = `name:starts_with:${searchQuery}`;
        response = await apiClientV2.searchCompaniesV2(params);
      } else if (type === 'position') {
        params.filter = `name:contains:${searchQuery}`;
        params.include_contact_count = true;
        response = await apiClientV2.searchPositionsV2(params);
      } else {
        throw new Error(`Unsupported autocomplete type: ${type}`);
      }

      // apiClientV2 resolves to the response body
      const responseData = response || {};
      const newSuggestions = responseData.data || [];
      const total = responseData.pagination?.total_items || 0;

      if (resetSuggestions) {
        setSuggestions(newSuggestions);
      } else {
        setSuggestions(prev => [...prev, ...newSuggestions]);
      }

      setTotalCount(total);
      setHasMore(newSuggestions.length === pageSize && (page * pageSize) < total);
      setCurrentPage(page);

    } catch (error) {
      if (error.name !== 'CanceledError') {
        console.error(`Autocomplete search error for ${type}:`, error);
        setSuggestions([]);
        setTotalCount(0);
        setHasMore(false);
      }
    } finally {
      setLoading(false);
      requestRef.current = null;
    }
  }, [type, minLength, pageSize, disabled]);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (query.length >= minLength) {
      debounceRef.current = setTimeout(() => {
        search(query, 1, true);
      }, debounceMs);
    } else {
      setSuggestions([]);
      setTotalCount(0);
      setHasMore(false);
      setCurrentPage(1);
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, minLength, debounceMs, search]);

  const loadMore = useCallback(() => {
    if (!loading && hasMore && query.length >= minLength) {
      search(query, currentPage + 1, false);
    }
  }, [loading, hasMore, query, minLength, search, currentPage]);

  const handlePageChange = useCallback((newPage) => {
    if (!loading && query.length >= minLength && newPage !== currentPage) {
      search(query, newPage, true);
    }
  }, [loading, query, minLength, currentPage, search]);

  const handleSelect = useCallback((suggestion) => {
    if (onSelect) {
      onSelect(suggestion);
    }
  }, [onSelect]);

  const clear = useCallback(() => {
    setQuery('');
    setSuggestions([]);
    setTotalCount(0);
    setHasMore(false);
    setCurrentPage(1);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      if (requestRef.current) {
        requestRef.current.cancel?.();
      }
    };
  }, []);

  return {
    query,
    suggestions,
    loading,
    totalCount,
    currentPage,
    hasMore,
    
    setQuery,
    loadMore,
    handlePageChange,
    handleSelect,
    clear,
    
    showSuggestions: suggestions.length > 0 || (loading && query.length >= minLength)
  };
};

export default useAutocomplete;
